import { Box, Button, FormControl, MenuItem, TextField } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useTranslation } from "hhn-commons";
import {
  ButtonComponent,
  FieldWrapper,
  FilterContainer,
  InputSearch,
  ResetButton,
} from "./ExportChatFilter.style";
import dayjs from "dayjs";
import { SearchBox } from "../common/SearchBox";
import { useState } from "react";

type ExportChatFilterProps = {
  onSetSearch: (value: string) => void;
  fromDate: number | undefined;
  onSetFromDate: (value: dayjs.Dayjs) => void;
  toDate: number | undefined;
  onSetToDate: (value: dayjs.Dayjs) => void;
  status: string | undefined;
  onSetStatus: (value: string | undefined) => void;
  onFilterButtonClick: () => void;
  onResetButtonClick: () => void;
};

export default function ExportChatFilter({
  onSetSearch,
  fromDate,
  onSetFromDate,
  toDate,
  onSetToDate,
  status,
  onSetStatus,
  onFilterButtonClick,
  onResetButtonClick,
}: ExportChatFilterProps) {
  const { t } = useTranslation();
  const tNs = (key: string) => t(key, { ns: "exportChat" });
  const statusOptions = [
    { label: "All", value: "ALL" },
    { label: "Failed", value: "FAILED" },
    { label: "In Progress", value: "IN_PROGRESS" },
    { label: "Completed", value: "COMPLETED" },
  ];

  const [searchText, setSearchText] = useState<string>("");

  const onSearchTextChange = (text: string) => {
    setSearchText(text);
    onSetSearch(text);
  };

  const onSearchClear = () => {
    setSearchText("");
    onSetSearch("");
  };
  return (
    <Box>
      <FieldWrapper>
        <InputSearch variant="outlined" fullWidth>
          <SearchBox
            placeholderLabel={tNs("searchPatient")}
            searchTestId="search-chat"
            searchText={searchText}
            onSearchTextChange={onSearchTextChange}
            onSearchClear={onSearchClear}
          />
        </InputSearch>
      </FieldWrapper>
      <FilterContainer>
        <FieldWrapper>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label={tNs("fromDate")}
              value={fromDate ? dayjs(fromDate) : null}
              onChange={value => onSetFromDate(dayjs(value))}
              maxDate={dayjs(toDate ? toDate : new Date())}
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: "small",
                  onKeyDown: event => {
                    event.preventDefault();
                  },
                  slotProps: {
                    htmlInput: {
                      "data-testid": "from-date-picker",
                    } as React.InputHTMLAttributes<HTMLInputElement>,
                    input: {
                      readOnly: true,
                    },
                  },
                },
              }}
            />
          </LocalizationProvider>
        </FieldWrapper>
        <FieldWrapper>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label={tNs("toDate")}
              value={toDate ? dayjs(toDate) : null}
              onChange={value => onSetToDate(dayjs(value))}
              maxDate={dayjs(new Date())}
              minDate={dayjs(fromDate)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: "small",
                  onKeyDown: event => {
                    event.preventDefault();
                  },
                  slotProps: {
                    htmlInput: {
                      "data-testid": "to-date-picker",
                    } as React.InputHTMLAttributes<HTMLInputElement>,
                    input: {
                      readOnly: true,
                    },
                  },
                },
              }}
            />
          </LocalizationProvider>
        </FieldWrapper>
        <FieldWrapper>
          <FormControl component="form" noValidate fullWidth autoComplete="off">
            <Box>
              <TextField
                //inputProps={{ "data-testid": "select-status" }}
                slotProps={{
                  htmlInput: { "data-testid": "select-status" },
                }}
                id="outlined-select-status"
                select
                label={tNs("status")}
                fullWidth
                size="small"
                value={status ?? "ALL"}
                onChange={e => {
                  const value =
                    e.target.value === "ALL" ? undefined : e.target.value;
                  onSetStatus(value);
                }}
              >
                {statusOptions.map(option => (
                  <MenuItem key={option.label} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          </FormControl>
        </FieldWrapper>
      </FilterContainer>
      <ButtonComponent>
        <ResetButton
          variant="outlined"
          size="small"
          onClick={onResetButtonClick}
          data-testid="reset-button"
        >
          {tNs("reset")}
        </ResetButton>
        <Button
          variant="contained"
          size="small"
          onClick={onFilterButtonClick}
          data-testid="filter-button"
        >
          {tNs("filter")}
        </Button>
      </ButtonComponent>
    </Box>
  );
}
